import type { MouseEvent } from 'react'
import { useCallback } from 'react'

import { useNavigate } from 'react-router'

import LeftChevronIcon from '@/assets/icons/icon-chevron-left.svg'
import RightChevronIcon from '@/assets/icons/icon-chevron-right.svg'
import ExpandMoreIcon from '@/assets/icons/icon-expand-more.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/collapse.tsx'
import { AppRoutes } from '@/utils/constants'
import { classNames } from '@/utils/helpers'

import { courseVideos } from '../data/course-videos.ts'
import { useCourse } from '../hooks/useCourse'
import { VideoItem } from './VideoItem'

export const Courses = () => {
  const { setCurrentVideo } = useCourse()
  const navigate = useNavigate()

  const handleVideoItemClick = useCallback(
    (id: string, category: string) => () => {
      setCurrentVideo(id)
      navigate(AppRoutes.Courses.Details.asUrl(category))
    },
    [navigate, setCurrentVideo],
  )

  const handleScrollButtonClick =
    (direction: 'right' | 'left') => (event: MouseEvent<HTMLButtonElement>) => {
      const { parentElement } = event.currentTarget

      parentElement?.scrollTo({
        left: parentElement.scrollLeft + (direction === 'left' ? -232 : +232),
        behavior: 'smooth',
      })
    }

  return (
    <div className="flex h-full w-full">
      <Accordion
        type="multiple"
        className="w-full"
        defaultValue={[courseVideos[0].name]}
      >
        {courseVideos.map((category, index) => (
          <AccordionItem
            key={category.name}
            value={category.name}
            className="relative w-full"
          >
            <AccordionTrigger
              showIcon={false}
              className="border-b-[1px] pl-1 pr-4 [&[data-state=open]>div>svg]:rotate-180"
            >
              <div className="flex items-center gap-2 font-medium text-title">
                <ExpandMoreIcon className="h-4 w-4 fill-secondary-500 transition-transform duration-200" />
                {category.name}
              </div>
              <div className="text-sm font-medium leading-6 text-secondary-300">
                {category.videos.length} Videos
              </div>
            </AccordionTrigger>
            <AccordionContent childClassName=" flex gap-[26px] pt-6 pb-8 mb-8 overflow-x-scroll max-w-[calc(100vw-72px)] lg:max-w-[calc(100vw-400px)]">
              <Button
                variant="secondary"
                size="icon"
                className={classNames(
                  '[>svg]:min-w-4 absolute left-0 top-[calc(35%+40px)] max-sm:hidden',
                  index === 0 && 'hidden',
                )}
                onClick={handleScrollButtonClick('left')}
              >
                <LeftChevronIcon className="text-neutral-default" />
              </Button>
              {category.videos.map((categoryVideo) => (
                <VideoItem
                  {...categoryVideo}
                  key={categoryVideo.video_id}
                  image={categoryVideo.thumbnail}
                  onClick={handleVideoItemClick(
                    categoryVideo.video_id,
                    category.name,
                  )}
                />
              ))}
              <Button
                variant="secondary"
                size="icon"
                className={classNames(
                  '[>svg]:min-w-4 absolute right-0 top-[calc(35%+40px)] max-sm:hidden',
                  {
                    hidden: index === 0,
                  },
                )}
                onClick={handleScrollButtonClick('right')}
              >
                <RightChevronIcon className="text-neutral-default" />
              </Button>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}
